import {
  Event as E,
  Id,
  Identifiable,
  List,
  list,
  Milestone,
  OptimizedGantt,
  Project,
  project,
  ProjectElementType,
  projectStatus,
  RankedElement,
  resource,
  Task,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { initialState } from "./initialState";
import { reduceList } from "../list";
import { reduceTask } from "../task";
import { reduceMilestone } from "../milestone";
import { reduceEvent } from "../event";
import { calculateAdvancement } from "../../utils/calculateAdvancement";
import { reduceProject } from "../project";

type ProjectPE = Extract<
  OptimizedGantt[number],
  { type: ProjectElementType.PROJECT }
>;
type ListPE = Extract<
  OptimizedGantt[number],
  { type: ProjectElementType.LIST }
>;
type TaskPE = Extract<
  OptimizedGantt[number],
  { type: ProjectElementType.TASK }
>;
type MilestonePE = Extract<
  OptimizedGantt[number],
  { type: ProjectElementType.MILESTONE }
>;
type EventPE = Extract<
  OptimizedGantt[number],
  { type: ProjectElementType.EVENT }
>;
type ModifiedElement = {
  endDate: string;
  startDate: string;
  minStartDate?: string;
  maxStartDate?: string;
  minEndDate?: string;
  maxEndDate?: string;
  type: ProjectElementType;
  uuid: Id;
};

function projectToProjectElement(
  project: Identifiable<Project>,
  inject: (projectElement: ProjectPE) => Partial<ProjectPE>,
): ProjectPE {
  const el: ProjectPE = {
    type: ProjectElementType.PROJECT,
    uuid: project.uuid,
    parentUuid: "",
    name: project.name,
    description: project.description,
    startDate: project.rootList.startDate,
    endDate: project.rootList.endDate,
    referenceKeyRank: 0,
    tags: project.tags,
    keyRank: 0,
    depth: 0,
    percentComplete: 0,
    completionDate: "",
    attributes: project.attributes || {},
    activityRootUuid: project.rootList.uuid,
    zone: project.zone,
    parentUuids: [],
  };

  return { ...el, ...inject(el) };
}

function listToProjectElement(
  list: Identifiable<List>,
  inject: (projectElement: ListPE) => Partial<ListPE>,
): ListPE {
  const el: ListPE = {
    type: ProjectElementType.LIST,
    uuid: list.uuid,
    parentUuid: list.parentList ? list.parentList.uuid : "",
    name: list.name,
    description: list.description,
    startDate: list.startDate,
    endDate: list.endDate,
    referenceKeyRank: list.activityKeyRank,
    tags: list.tags,
    keyRank: 0,
    depth: 0,
    percentComplete: 0,
    completionDate: "",
    attributes: list.attributes || {},
    parentUuids: [],
    activityRootUuid: "",
    minStartDate: list.minStartDate,
    maxStartDate: list.maxStartDate,
    minEndDate: list.minEndDate,
    maxEndDate: list.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function taskToProjectElement(
  task: Identifiable<Task>,
  inject: (projectElement: TaskPE) => Partial<TaskPE>,
): TaskPE {
  const el: TaskPE = {
    type: ProjectElementType.TASK,
    uuid: task.uuid,
    name: task.name,
    description: task.description,
    startDate: task.startDate,
    endDate: task.endDate,
    referenceKeyRank: task.activityKeyRank,
    tags: task.tags,
    allocations: task.allocations,
    status: task.status,
    percentComplete: task.percentComplete,
    completionDate: task.completionDate,
    attributes: task.attributes || {},
    depth: 0,
    keyRank: 0,
    parentUuid: "",
    activityRootUuid: "",
    parentUuids: [],
    minStartDate: task.minStartDate,
    maxEndDate: task.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function milestoneToProjectElement(
  milestone: Identifiable<Milestone>,
  inject: (projectElement: MilestonePE) => Partial<MilestonePE>,
): MilestonePE {
  const el: MilestonePE = {
    type: ProjectElementType.MILESTONE,
    uuid: milestone.uuid,
    name: milestone.name,
    description: milestone.description,
    startDate: milestone.endDate,
    endDate: milestone.endDate,
    referenceKeyRank: milestone.keyRank,
    tags: milestone.tags,
    state: milestone.state,
    percentComplete: milestone.state === "DONE" ? 100 : 0,
    completionDate: milestone.completedAt,
    attributes: milestone.attributes || {},
    depth: 0,
    keyRank: 0,
    parentUuid: "",
    activityRootUuid: "",
    parentUuids: [],
    minStartDate: milestone.minStartDate,
    maxEndDate: milestone.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function eventToProjectElement(
  event: Identifiable<E>,
  inject: (projectElement: EventPE) => Partial<EventPE>,
): EventPE {
  const endTime = !!event.endTime ? new Date(event.endTime) : new Date();
  const el: EventPE = {
    type: ProjectElementType.EVENT,
    uuid: event.uuid,
    name: event.name,
    description: event.description,
    startDate: event.startTime,
    endDate: event.endTime,
    referenceKeyRank: event.keyRank,
    tags: event.tags,
    percentComplete: !!event.endTime && endTime < new Date() ? 100 : 0,
    completionDate: event.endTime,
    attributes: event.attributes || {},
    parentUuid: "",
    activityRootUuid: "",
    keyRank: 0,
    depth: 0,
    parentUuids: [],
    minStartDate: event.minStartDate,
    maxEndDate: event.maxEndDate,
  };

  return { ...el, ...inject(el) };
}

function projectElementToProject(element: ProjectPE): Identifiable<Project> {
  return {
    ...project,
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    rootList: {
      ...list,
      uuid: element.activityRootUuid,
      startDate: element.startDate,
      endDate: element.endDate,
    },
    status: {
      ...projectStatus,
      uuid: "",
    },
    createdDate: "",
    tags: element.tags,
    attributes: element.attributes,
  };
}

function projectElementToList(element: ListPE): Identifiable<List> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startDate: element.startDate,
    endDate: element.endDate,
    createdDate: "",
    parentList: { ...list, uuid: element.parentUuid },
    subLists: [],
    tasks: [],
    events: [],
    milestones: [],
    tags: element.tags,
    attachments: [],
    activityKeyRank: element.referenceKeyRank,
    statusKeyRank: 0,
    attributes: element.attributes,
    minStartDate: element.minStartDate ?? "",
    maxStartDate: element.maxStartDate ?? "",
    minEndDate: element.minEndDate ?? "",
    maxEndDate: element.maxEndDate ?? "",
  };
}

function projectElementToTask(element: TaskPE): Identifiable<Task> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startDate: element.startDate,
    endDate: element.endDate,
    createdDate: "",
    completionDate: element.completionDate,
    percentComplete: element.percentComplete,
    allocations: element.allocations,
    status: element.status,
    tags: element.tags,
    attachments: [],
    activityKeyRank: element.referenceKeyRank,
    statusKeyRank: 0,
    attributes: element.attributes,
    duration: 0,
    minStartDate: element.minStartDate ?? "",
    maxEndDate: element.maxEndDate ?? "",
    lastModified: "",
  };
}

function projectElementToEvent(element: EventPE): Identifiable<E> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startTime: element.startDate,
    endTime: element.endDate,
    createdDate: "",
    organizer: { ...resource, uuid: "" },
    participants: [],
    tags: element.tags,
    attachments: [],
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
    minStartDate: element.minStartDate,
    maxEndDate: element.maxEndDate,
  };
}

function projectElementToMilestone(
  element: MilestonePE,
): Identifiable<Milestone> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    endDate: element.endDate,
    completedAt: element.completionDate,
    createdDate: "",
    list: { ...list, uuid: element.parentUuid },
    state: element.state,
    tags: element.tags,
    attachments: [],
    commentsNbr: 0,
    attachmentsNbr: 0,
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
    minStartDate: element.minStartDate ?? "",
    maxEndDate: element.maxEndDate ?? "",
  };
}

function isParent(
  element: OptimizedGantt[number],
  parent: OptimizedGantt[number],
) {
  return (
    parent.uuid === element.parentUuid ||
    (parent.type === ProjectElementType.PROJECT &&
      parent.activityRootUuid === element.parentUuid)
  );
}

function isProject({ type }: OptimizedGantt[number]) {
  return type === ProjectElementType.PROJECT;
}

function findScopeEnd(gantt: OptimizedGantt, index: number): number {
  if (isProject(gantt[index])) {
    for (let i = index + 1; i < gantt.length; i++) {
      if (isProject(gantt[i])) return i;
    }
    return gantt.length;
  }

  const depth = gantt[index].depth;
  let i = index + 1;
  while (i < gantt.length && gantt[i].depth > depth) {
    i++;
  }
  return i;
}

function placeElement(
  createdElement: OptimizedGantt[number],
  gantt: OptimizedGantt,
  project: Identifiable<Project>,
  range?: {
    min: RankedElement<any> | null;
    max: RankedElement<any> | null;
  },
): OptimizedGantt {
  const parentIndex = gantt.findIndex((el) => isParent(createdElement, el));

  if (parentIndex === -1) {
    if (gantt.length <= 1 && gantt[0]?.uuid === project.uuid) {
      return [...gantt, { ...createdElement, depth: 0 }];
    }
    return gantt;
  }

  const parent = gantt[parentIndex];
  const depth = isProject(parent) ? 0 : parent.depth + 1;
  const element = { ...createdElement, depth };
  const parentEnd = findScopeEnd(gantt, parentIndex);

  let insertIndex: number | null = null;

  if (range?.min) {
    const minIndex = gantt.findIndex((el) => el.uuid === range.min!.uuid);
    if (minIndex !== -1 && minIndex >= parentIndex && minIndex < parentEnd) {
      insertIndex = findScopeEnd(gantt, minIndex);
    }
  }

  if (insertIndex === null && range?.max) {
    const maxIndex = gantt.findIndex((el) => el.uuid === range.max!.uuid);
    if (maxIndex !== -1 && maxIndex >= parentIndex && maxIndex < parentEnd) {
      insertIndex = maxIndex;
    }
  }

  if (insertIndex === null) {
    for (let i = parentIndex + 1; i < parentEnd; i++) {
      if (
        gantt[i].depth === depth &&
        gantt[i].referenceKeyRank >= element.referenceKeyRank
      ) {
        insertIndex = i;
        break;
      }
    }
  }

  if (insertIndex === null) {
    insertIndex = parentEnd;
  }

  return [...gantt.slice(0, insertIndex), element, ...gantt.slice(insertIndex)];
}

function shiftDate(date: Date, shift: number) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + shift,
  ).toISOString();
}

function getShift(before: List, after: List) {
  return Math.round(
    (new Date(after.endDate).getTime() - new Date(before.endDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

function getStartShift(before: List, after: List) {
  return Math.round(
    (new Date(after.startDate).getTime() -
      new Date(before.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
}

function shiftElement(
  element: OptimizedGantt[number],
  shift: number,
): OptimizedGantt[number] {
  const endDate = element.endDate ? new Date(element.endDate) : new Date();
  const startDate = element.startDate
    ? new Date(element.startDate)
    : new Date();

  return {
    ...element,
    endDate: element.endDate ? shiftDate(endDate, shift) : element.endDate,
    startDate: element.startDate
      ? shiftDate(startDate, shift)
      : element.startDate,
  };
}

function applyModifiedElements(
  gantt: OptimizedGantt,
  modifiedElements: ModifiedElement[],
) {
  return modifiedElements.length === 0
    ? gantt
    : gantt.map((el) => {
        const foundModEl = modifiedElements.find(({ uuid }) =>
          el.type === ProjectElementType.PROJECT
            ? uuid === el.uuid || el.activityRootUuid === uuid
            : uuid === el.uuid,
        );

        return foundModEl
          ? {
              ...el,
              startDate: foundModEl.startDate,
              endDate: foundModEl.endDate,
              ...(foundModEl.minStartDate !== undefined && {
                minStartDate: foundModEl.minStartDate,
              }),
              ...(foundModEl.maxStartDate !== undefined && {
                maxStartDate: foundModEl.maxStartDate,
              }),
              ...(foundModEl.minEndDate !== undefined && {
                minEndDate: foundModEl.minEndDate,
              }),
              ...(foundModEl.maxEndDate !== undefined && {
                maxEndDate: foundModEl.maxEndDate,
              }),
            }
          : el;
      });
}

function propagateEvent(
  optimizedGantt: OptimizedGantt = initialState,
  event: Event,
): OptimizedGantt {
  const [createdElement, project, range] =
    event.type === events.createTaskSucceeded
      ? [
          taskToProjectElement(event.data.task, () => ({
            parentUuid: event.data.list.uuid,
            percentComplete: calculateAdvancement(event.data.task)
              .percentComplete,
          })),
          event.data.project,
          event.data.range,
        ]
      : event.type === events.createKanbanTaskSucceeded
        ? [
            taskToProjectElement(event.data.task, () => ({
              parentUuid: event.data.list.uuid,
              percentComplete: calculateAdvancement(event.data.task)
                .percentComplete,
            })),
            event.data.project,
            undefined,
          ]
        : event.type === events.createListSucceeded
          ? [
              listToProjectElement(event.data.list, () =>
                event.data.list.parentList
                  ? { parentUuid: event.data.list.parentList.uuid }
                  : {},
              ),
              event.data.project,
              event.data.range,
            ]
          : event.type === events.createMilestoneSucceeded
            ? [
                milestoneToProjectElement(event.data.milestone, () => ({
                  parentUuid: event.data.list.uuid,
                })),
                event.data.project,
                event.data.range,
              ]
            : event.type === events.createEventSucceeded
              ? [
                  eventToProjectElement(event.data.event, () => ({
                    parentUuid: event.data.list.uuid,
                  })),
                  event.data.project,
                  event.data.range,
                ]
              : [null, null, null];

  const modifiedElements =
    typeof event === "object" &&
    "data" in event &&
    !!event.data &&
    typeof event.data === "object" &&
    "modifiedElements" in event.data &&
    typeof event.data.modifiedElements === "object"
      ? (Object.values(event.data.modifiedElements).flat() as ModifiedElement[])
      : [];
  const removedElement: Identifiable<any> =
    event.type === events.removeTaskSucceeded
      ? event.data
      : event.type === events.removeListSucceeded
        ? event.data
        : event.type === events.removeMilestoneSucceeded
          ? event.data
          : event.type === events.removeEventSucceeded
            ? event.data
            : null;
  const isEditingList =
    event.type === events.editListRequested ||
    event.type === events.editListFailed;
  const isMovingList =
    isEditingList &&
    getStartShift(event.data.list, event.data.nextList) !== 0 &&
    getStartShift(event.data.list, event.data.nextList) ===
      getShift(event.data.list, event.data.nextList);

  return applyModifiedElements(
    !!createdElement
      ? placeElement(createdElement, optimizedGantt, project, range)
      : removedElement
        ? optimizedGantt.filter(
            (projectElement) => projectElement.uuid !== removedElement.uuid,
          )
        : optimizedGantt.reduce<{
            gantt: OptimizedGantt;
            movedList: OptimizedGantt[number] | null;
          }>(
            (acc, projectElement) => {
              const nextProjectElement =
                projectElement.type === ProjectElementType.PROJECT
                  ? projectToProjectElement(
                      reduceProject(
                        projectElementToProject(projectElement),
                        event,
                      ),
                      () => ({
                        depth: projectElement.depth,
                        percentComplete: projectElement.percentComplete,
                        completionDate: projectElement.completionDate,
                        keyRank: projectElement.keyRank,
                        parentUuids: projectElement.parentUuids,
                        activityRootUuid: projectElement.activityRootUuid,
                        minStartDate: projectElement.minStartDate,
                        maxStartDate: projectElement.maxStartDate,
                        minEndDate: projectElement.minEndDate,
                        maxEndDate: projectElement.maxEndDate,
                      }),
                    )
                  : projectElement.type === ProjectElementType.LIST
                    ? listToProjectElement(
                        reduceList(projectElementToList(projectElement), event),
                        () => ({
                          depth: projectElement.depth,
                          percentComplete: projectElement.percentComplete,
                          completionDate: projectElement.completionDate,
                          keyRank: projectElement.keyRank,
                          parentUuids: projectElement.parentUuids,
                          activityRootUuid: projectElement.activityRootUuid,
                        }),
                      )
                    : projectElement.type === ProjectElementType.TASK
                      ? taskToProjectElement(
                          reduceTask(
                            projectElementToTask(projectElement),
                            event,
                          ),
                          () => ({
                            depth: projectElement.depth,
                            parentUuid: projectElement.parentUuid,
                            keyRank: projectElement.keyRank,
                            parentUuids: projectElement.parentUuids,
                            activityRootUuid: projectElement.activityRootUuid,
                          }),
                        )
                      : projectElement.type === ProjectElementType.MILESTONE
                        ? milestoneToProjectElement(
                            reduceMilestone(
                              projectElementToMilestone(projectElement),
                              event,
                            ),
                            () => ({
                              depth: projectElement.depth,
                              parentUuid: projectElement.parentUuid,
                              keyRank: projectElement.keyRank,
                              parentUuids: projectElement.parentUuids,
                              activityRootUuid: projectElement.activityRootUuid,
                            }),
                          )
                        : projectElement.type === ProjectElementType.EVENT
                          ? eventToProjectElement(
                              reduceEvent(
                                projectElementToEvent(projectElement),
                                event,
                              ),
                              () => ({
                                depth: projectElement.depth,
                                parentUuid: projectElement.parentUuid,
                                keyRank: projectElement.keyRank,
                                parentUuids: projectElement.parentUuids,
                                activityRootUuid:
                                  projectElement.activityRootUuid,
                              }),
                            )
                          : projectElement;
              const isResettingList =
                !!acc.movedList &&
                (acc.movedList.type === ProjectElementType.PROJECT
                  ? projectElement.type === ProjectElementType.PROJECT
                  : projectElement.depth <= acc.movedList.depth);

              return {
                ...acc,
                movedList: isResettingList
                  ? null
                  : isMovingList &&
                      (projectElement.uuid === event.data.list.uuid ||
                        (projectElement.type === ProjectElementType.PROJECT &&
                          projectElement.activityRootUuid ===
                            event.data.list.uuid))
                    ? projectElement
                    : acc.movedList,
                gantt: [
                  ...acc.gantt,
                  isEditingList && acc.movedList && !isResettingList
                    ? shiftElement(
                        nextProjectElement,
                        event.type === events.editListFailed
                          ? getShift(event.data.nextList, event.data.list)
                          : getShift(event.data.list, event.data.nextList),
                      )
                    : nextProjectElement,
                ],
              };
            },
            { movedList: null, gantt: [] },
          ).gantt,
    modifiedElements ?? [],
  );
}

export { propagateEvent };
